<template>
  <div aria-label="radio-group" class="v-radio-group" :class="classes" role="group">
    <slot />

    <span v-if="classes['has-suffix']" class="v-radio-group__suffix-inner">
      <VIconHelp v-if="help" :help icon-class="v-input__icon" type="placeholder" />
      <span v-if="optional" class="v-radio-group__suffix-optional">{{ i18n.optional }}</span>
    </span>

    <VErrors :errors />
  </div>
</template>

<script setup lang="ts">
import { computed, provide } from 'vue'

import { useErrors } from '../composables/useErrors.ts'
import type { VRadioType } from '../types.ts'
import { i18nInput as i18n } from '../utils/i18n.ts'
import { $radioGroupKey } from '../utils/injections.ts'
import VErrors from './VErrors.vue'
import VIconHelp from './VIconHelp.vue'

export interface Props {
  disabled?: boolean
  error?: string | string[]
  help?: string | string[]
  modelValue?: any
  optional?: boolean
  prop?: string
  type?: VRadioType
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  modelValue: undefined,
  type: '',
  error: '',
  prop: '',
  help: '',
})

const emit = defineEmits<{
  'update:modelValue': [modelValue: any]
}>()

defineSlots<{
  default?(): any
}>()

const classes = computed(() => ({
  [`v-radio-group--${props.type}`]: props.type,
  ['has-suffix']: props.help || props.optional,
}))
const errors = useErrors(props)

function input(value: any) {
  emit('update:modelValue', value)
}

provide($radioGroupKey, {
  props,
  input,
})
</script>

<style>
.v-radio-group {
  position: relative;
}
.v-radio-group.has-suffix {
  width: 100%;
}
.v-radio-group__suffix-inner {
  display: flex;
  align-items: center;
  float: right;
}
.v-radio-group__suffix-optional {
  color: var(--color-text-placeholder);
}
</style>
